import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import {
  SuccessFilledIcon,
  WarningFilledIcon,
} from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import { isNil } from 'lodash';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { getSubmissionReadinessAction } from 'store/loanApplications/actions';
import { selectSubmissionReadinessByApplicationId } from 'store/loanApplications/selectors';

import {
  SUBMISSION_READINESS_COPY,
  SUBMISSION_READINESS_TEST_IDS,
} from './SubmissionReadinessPanel.constants';
import {
  categoryGroupStyles,
  categoryListStyles,
  headerStyles,
  itemDotStyles,
  itemRowStyles,
  missingIconStyles,
  panelStyles,
  readyIconStyles,
} from './SubmissionReadinessPanel.styles';
import { groupMissingItemsByCategory } from './SubmissionReadinessPanel.utils';

const SubmissionReadinessPanel = ({ applicationId }) => {
  const dispatch = useDispatch();

  const readiness = useSelector(selectSubmissionReadinessByApplicationId(applicationId));

  const groupedMissingItems = useMemo(
    () => groupMissingItemsByCategory(readiness?.missingItems),
    [readiness?.missingItems],
  );

  useEffectSafeStrictMode(() => {
    if (!isNil(applicationId)) {
      dispatch(getSubmissionReadinessAction({ applicationId }));
    }
  }, [applicationId, dispatch]);

  // No data yet, a degraded 403, or no configured requirements
  // (evaluatedAgainst === null) → render nothing (honest empty).
  if (isNil(readiness) || isNil(readiness.evaluatedAgainst)) {
    return null;
  }

  if (readiness.ready) {
    return (
      <Box data-testid={SUBMISSION_READINESS_TEST_IDS.READY} sx={panelStyles}>
        <Box sx={headerStyles}>
          <Box component="span" sx={readyIconStyles}>
            <SuccessFilledIcon />
          </Box>
          <Typography variant="medium16">{SUBMISSION_READINESS_COPY.READY_TITLE}</Typography>
        </Box>
        <Typography color="text.secondary" variant="regular14">
          {SUBMISSION_READINESS_COPY.READY_SUBTITLE}
        </Typography>
      </Box>
    );
  }

  return (
    <Box data-testid={SUBMISSION_READINESS_TEST_IDS.MISSING} sx={panelStyles}>
      <Box sx={headerStyles}>
        <Box component="span" sx={missingIconStyles}>
          <WarningFilledIcon />
        </Box>
        <Typography variant="medium16">{SUBMISSION_READINESS_COPY.MISSING_TITLE}</Typography>
      </Box>
      <Box sx={categoryListStyles}>
        {groupedMissingItems.map(({ categoryLabel, items }) => (
          <Box
            data-testid={SUBMISSION_READINESS_TEST_IDS.CATEGORY}
            key={categoryLabel}
            sx={categoryGroupStyles}
          >
            <Typography color="text.secondary" variant="medium12">
              {categoryLabel}
            </Typography>
            {items.map(({ itemId, label }) => (
              <Box data-testid={SUBMISSION_READINESS_TEST_IDS.ITEM} key={itemId} sx={itemRowStyles}>
                <Box sx={itemDotStyles} />
                <Typography variant="regular14">{label}</Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

SubmissionReadinessPanel.propTypes = {
  applicationId: PropTypes.string.isRequired,
};

export default SubmissionReadinessPanel;
